import AdminState from '../AdminState';
import useAdminToast from "../useAdminToast";
import { AdminAlertIcon as AlertIcon, CloseIcon, RepeatIcon } from '../AdminIcon';
// Components/Tabs/ActiveSessionsTab.jsx

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import { AdminToolbar } from '../AdminLayout/AdminLayout';
import Backend from "../../../utils/api.js";
import { Box, Text, VStack, HStack, Badge, Button, Alert, AlertTitle, AlertDescription, IconButton, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton } from '@chakra-ui/react';

import AdminUserDetailsTab from "./AdminUserDetailsTab.jsx";
import "./ActiveSessionsTab.css";

/**
 * ActiveSessionsTab Component
 * 
 * Displays all users currently checked into the selected lab with the ability
 * to check out individual users or all users at once.
 */
function ActiveSessionsTab({ user, labId, pushTab, revert }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [userBadges, setUserBadges] = useState({}); // Map of UID -> badges array
  // UID -> badges already fetched this mount. Every SSE event triggers a
  // silent reload; without this cache each one re-queried Parchment for every
  // active session (~20k live calls/week from one admin tab).
  const badgeCacheRef = useRef({});

  const { isOpen: isLogoutOpen, onOpen: onLogoutOpen, onClose: onLogoutClose } = useDisclosure();
  const { isOpen: isLogoutAllOpen, onOpen: onLogoutAllOpen, onClose: onLogoutAllClose } = useDisclosure();
  const toast = useAdminToast();

  useEffect(() => {
    loadSessions();

    const es = Backend.subscribeToSpaceEvents(labId, (event) => {
      if (
        event.event === 'session_ended' ||
        event.event === 'session_updated' ||
        event.event === 'space_snapshot'
      ) {
        loadSessionsSilent();
      }
    });

    return () => es.close();
  }, [labId]);

  // Helper function to fetch badges for a list of sessions. earned_badges
  // returns {data: [badge dicts with name]}; tool_permissions (used here
  // previously) returns bare tool IDs, so no badges ever rendered.
  // With onlyNew, users already in the cache are not re-fetched.
  const fetchUserBadges = async (sessionsList, { onlyNew = false } = {}) => {
    if (!onlyNew) badgeCacheRef.current = {};
    const cache = badgeCacheRef.current;
    const toFetch = [...new Set(sessionsList.map((s) => s.user_id))]
      .filter((uid) => !(uid in cache));

    await Promise.all(toFetch.map(async (uid) => {
      try {
        const badgeRes = await Backend.getUserEarnedBadges(uid);
        cache[uid] = badgeRes.status === 200
          ? (badgeRes.data || []).filter((item) => item && item.name)
          : [];
      } catch (err) {
        cache[uid] = [];
      }
    }));

    const badgesMap = {};
    sessionsList.forEach(({ user_id }) => {
      badgesMap[user_id] = cache[user_id] || [];
    });
    setUserBadges(badgesMap);
  };

  const loadSessions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await Backend.adminGetActiveSessionsV2(labId);

      if (response.status === 200) {
        setHasLoaded(true);
        const sessionsList = response.data || [];
        setSessions(sessionsList);
        await fetchUserBadges(sessionsList);
      } else if (response.status === 403) {
        setError("You do not have permission to view active sessions");
      } else {
        setError("Failed to load active sessions");
      }
    } catch (err) {
      console.error("Error loading sessions:", err);
      setError("Failed to connect to the server");
    } finally {
      setLoading(false);
    }
  };

  const loadSessionsSilent = async () => {
    try {
      const response = await Backend.adminGetActiveSessionsV2(labId);
      if (response.status === 200) {
        const sessionsList = response.data || [];
        setSessions(sessionsList);
        await fetchUserBadges(sessionsList, { onlyNew: true });
      }
    } catch (err) {
      console.error("Error refreshing sessions:", err);
    }
  };

  const handleForceLogout = async () => {
    if (!selectedUser) return;

    setLoggingOut(true);
    try {
      const response = await Backend.adminForceLogout(user.UID, selectedUser.user_id, labId);

      if (response.status === 200) {
        toast({
          title: "User checked out",
          description: `${selectedUser.first_name} ${selectedUser.last_name} has been checked out.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onLogoutClose();
        await loadSessions();
      } else {
        toast({
          title: "Error",
          description: "Failed to check out user",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error forcing logout:", err);
      toast({
        title: "Error",
        description: "Failed to connect to the server",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoggingOut(false);
    }
  };

  const handleForceLogoutAll = async () => {
    setLoggingOutAll(true);
    try {
      const response = await Backend.adminForceLogoutAll(user.UID, labId);

      if (response.status === 200) {
        toast({
          title: "All users checked out",
          description: `${response.users_affected || sessions.length} user(s) have been checked out.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onLogoutAllClose();
        await loadSessions();
      } else {
        toast({
          title: "Error",
          description: "Failed to check out users",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error forcing logout all:", err);
      toast({
        title: "Error",
        description: "Failed to connect to the server",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoggingOutAll(false);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const openLogoutModal = (session) => {
    setSelectedUser(session);
    onLogoutOpen();
  };

  const handleViewUserInfo = (session) => {
    const targetUser = {
      UID: session.user_id,
      firstName: session.first_name,
      lastName: session.last_name,
      email: "",
      directoryID: "",
    };
    
    pushTab(
      <AdminUserDetailsTab adminRoute="user"
        key={`adminUserDetails-${session.user_id}`}
        user={user}
        targetUser={targetUser}
        labId={labId}
        pushTab={pushTab}
        revert={revert}
      />
    );
  };

  return (
    <Slider
      name="Active sessions"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className={`ActiveSessionsTab${loading && hasLoaded ? ' admin-refreshing' : ''}`} aria-busy={loading}>
        <AdminToolbar title={`${labId} - Active sessions`} count={<Badge colorScheme="green">{sessions.length} user{sessions.length !== 1 ? 's' : ''}</Badge>}>

            <IconButton
              icon={<RepeatIcon />}
              onClick={loadSessions}
              aria-label="Refresh"
              isLoading={loading}
            />
            {sessions.length > 0 && (
              <Button
                colorScheme="blue"
                variant="solid"
                size="md"
                onClick={onLogoutAllOpen}
              >
                Check out all
              </Button>
            )}
        </AdminToolbar>

        {/* Error Display */}
        {error && (
          <AdminState status="error" title="Active sessions could not be loaded" description={error} onRetry={loadSessions} />
        )}

        {/* Loading State */}
        {loading && !hasLoaded ? (
          <AdminState status="loading" title="Loading active sessions…" />
        ) : error ? null : sessions.length === 0 ? (
          <AdminState title="No active sessions" description="People currently checked in to this space will appear here." />
        ) : (
          <VStack align="stretch" spacing={3} className="sessions-list">
            {sessions.map((session) => {
              const badges = userBadges[session.user_id] || [];
              return (
                <Box key={`${session.user_id}-${session.started_at}`} className="session-card">
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between" className="admin-card-layout">
                      <VStack align="start" spacing={1} minW={0} flex={1}>
                        <Text fontWeight="semibold" fontSize="md">
                          {session.first_name} {session.last_name}
                        </Text>
                        <HStack spacing={0} gap={2} flexWrap="wrap" fontSize="sm" color="gray.600">
                          <Text>UID: {session.user_id}</Text>
                          <Text>•</Text>
                          <Text>Checked in: {formatTime(session.started_at)}</Text>
                          <Text>•</Text>
                          <Badge colorScheme="blue">
                            {formatDuration(session.duration_minutes)} ago
                          </Badge>
                          {session.previous_visits !== undefined && (
                            <>
                              <Text>•</Text>
                              <Text>
                                {session.previous_visits} previous visit{session.previous_visits !== 1 ? 's' : ''}
                              </Text>
                            </>
                          )}
                          <Text>•</Text>
                          <Text title={session.reason || "No purpose provided"} maxW="200px" isTruncated color={session.reason ? "gray.600" : "gray.600"}>
                            Purpose: {session.reason && session.reason !== "undefined" ? session.reason : "No purpose selected"}
                          </Text>
                        </HStack>
                        {badges.length > 0 && (
                          <HStack spacing={2} mt={1} flexWrap="wrap">
                            <Text fontSize="xs" color="gray.600">Badges:</Text>
                            {badges.map((badge, idx) => (
                              <Badge key={idx} className="admin-entity-badge" colorScheme="purple" fontSize="xs">
                                {badge.name || badge.machineType || 'Badge'}
                              </Badge>
                            ))}
                          </HStack>
                        )}
                        {session.reserved_tool_instances && session.reserved_tool_instances.length > 0 && (
                          <HStack spacing={2} mt={1} flexWrap="wrap">
                            <Text fontSize="xs" color="gray.600">Tool instances in use:</Text>
                            {session.reserved_tool_instances.map((tool, idx) => (
                              <Badge key={idx} className="admin-entity-badge" colorScheme="teal" fontSize="xs">
                                {tool.name}
                              </Badge>
                            ))}
                          </HStack>
                        )}
                      </VStack>

                      <HStack spacing={2} className="admin-card-actions">
                        <Button
                          colorScheme="blue"
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewUserInfo(session)}
                        >
                          User info
                        </Button>
                        <Button
                          colorScheme="blue"
                          size="sm"
                          onClick={() => openLogoutModal(session)}
                        >
                          Check out
                        </Button>
                      </HStack>
                    </HStack>
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        )}

        {/* Check out Single User Modal */}
        <Modal isOpen={isLogoutOpen} onClose={onLogoutClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Check out user</ModalHeader>
            <ModalCloseButton ><CloseIcon /></ModalCloseButton>
            <ModalBody>
              <Text>
                Are you sure you want to check out{" "}
                <strong>
                  {selectedUser?.first_name} {selectedUser?.last_name}
                </strong>{" "}
                from {labId}?
              </Text>
              <Text mt={2} color="orange.600" fontSize="sm">
                This will immediately end their session. They will need to check in
                again to continue using the space.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onLogoutClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleForceLogout}
                isLoading={loggingOut}
              >
                Check out
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Check out all Modal */}
        <Modal isOpen={isLogoutAllOpen} onClose={onLogoutAllClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Check out all users</ModalHeader>
            <ModalCloseButton ><CloseIcon /></ModalCloseButton>
            <ModalBody>
              <Alert status="warning" mb={4}>
                <AlertIcon status="warning" />
                <Box>
                  <AlertTitle>Warning</AlertTitle>
                  <AlertDescription>
                    This will immediately end all active sessions in {labId}.
                  </AlertDescription>
                </Box>
              </Alert>
              <Text>
                Are you sure you want to check out all{" "}
                <strong>{sessions.length}</strong> user(s) from {labId}?
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onLogoutAllClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleForceLogoutAll}
                isLoading={loggingOutAll}
              >
                Check out all
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </Slider>
  );
}

ActiveSessionsTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default ActiveSessionsTab;
